// Copyright 2022 Sorama B.V.

import { BindingEngine, bindable, inject, observable, transient } from "aurelia-framework";
import { FrontEnd } from "datatypes/SoramaLocalApi";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { FirmwareRepository } from "repositories/FirmwareRepository";
import { CamIV64DeviceType } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { BinaryData } from "units/Data";

enum FirmwareUpdateState {
    Init,
    Downloading,
    Uploading,
    Succeeded,
    Failed,
}
@inject(BindingEngine, "CamService", ICamApi, FirmwareRepository)
@transient()
export class DeviceFirmwareUpdateModule implements IWorkflowModuleLifetime {
    private moduleInstance: IWorkflowModuleInstance;

    @bindable
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public frontEndId: string;

    private loadingFrontEnd = true;
    private frontEnd: FrontEnd;

    private readonly deviceKey: CamIV64DeviceType = "iV64";

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public allowLocalFile = false;

    private firmwareFileUpload: FileList;

    private installedVersion: string;
    private remoteVersion: string;

    @observable
    private updateState: FirmwareUpdateState = FirmwareUpdateState.Init;

    private progressDownloadedMB = 0;
    private progressUploadedMB = 0;
    private totalMB = 0;
    private downloadProgressBar = 0;
    private uploadProgressBar = 0;
    private placeHolderDownloadSize = 600;

    private errorMessage: string;

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly camService: IHardwareService<FrontEnd>,
        private readonly camApi: ICamApi,
        private readonly firmwareRepository: FirmwareRepository
    ) {}

    public async activateModule(moduleInstance: IWorkflowModuleInstance) {
        this.moduleInstance = moduleInstance;

        this.firmwareRepository.getLatestVersion(this.deviceKey).then((version) => {
            this.remoteVersion = version;
        });

        await this.camService.refresh();
        this.frontEnd = await this.camService.getHardwareById(this.frontEndId);
        this.loadingFrontEnd = false;
        this.onDeviceStatusChanged();
    }

    public deactivateModule(): void {}

    private onDeviceStatusChanged() {
        this.installedVersion = this.frontEnd.frontEndConnectors[0].version;
    }

    private async updateFirmware() {
        let firmwareFile: Blob;
        if (this.allowLocalFile) {
            firmwareFile = this.firmwareFileUpload.item(0);
        } else {
            firmwareFile = await this.downloadFirmware(this.remoteVersion);
        }
        await this.uploadFirmware(firmwareFile);
    }

    private async downloadFirmware(version: string) {
        this.updateState = FirmwareUpdateState.Downloading;
        this.totalMB =
            (await this.firmwareRepository.getFirmwareSize(this.deviceKey, version)) / BinaryData.mebibyte.conversion;

        // Temporary fix when the download size cannot be retrieved, gives the user a rough time indication
        if (this.totalMB <= 0) this.totalMB = this.placeHolderDownloadSize;

        return this.firmwareRepository.downloadFirmware(
            this.deviceKey,
            this.remoteVersion,
            (progress: ProgressIndicator) => {
                this.progressDownloadedMB = progress.progressEvent.loaded / BinaryData.mebibyte.conversion;
                this.downloadProgressBar = this.progressDownloadedMB / this.totalMB;
            }
        );
    }

    private async uploadFirmware(firmwareFile: Blob) {
        this.updateState = FirmwareUpdateState.Uploading;

        this.camApi
            .updateDeviceFirmware(firmwareFile, (progress: ProgressIndicator) => {
                this.progressUploadedMB = progress.progressEvent.loaded / BinaryData.mebibyte.conversion;
                this.totalMB = progress.progressEvent.total / BinaryData.mebibyte.conversion;
                this.uploadProgressBar = this.progressUploadedMB / this.totalMB;
            })
            .then((res) => {
                this.updateState = FirmwareUpdateState.Succeeded;
            })
            .catch((e) => {
                this.updateState = FirmwareUpdateState.Failed;
                this.errorMessage = e.message;
            });
    }

    private retry() {
        this.updateState = FirmwareUpdateState.Init;
    }

    private toStateEnum(name: string) {
        return (<unknown>FirmwareUpdateState)[name];
    }
}
